import { z } from 'zod';

import { PatchDefaultHeaders } from '@@api/headers';
import { defineService } from '@@api/lib/defineService';
import {
    Agency,
    LoginPayloadResponse,
    NewUser,
    Preference,
    UnityUser,
    User,
} from '@@api/services/auth/schemas';
import { deserializeUser, serializeUser } from '@@api/services/auth/transformers';
import { PaginationParams, SearchParams } from '@@api/utils/schemas/queryParams';
import { paginated } from '@@api/utils/schemas/utils';
import config from '@@config';
import { USER_SOURCES } from '@@containers/users/constants';

export const authService = defineService(
    (defineEndpoint) => ({
        users: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/resources/users',
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                    ...PaginationParams.shape,
                    serviceUser: z.boolean().optional(),
                    userSource: z.nativeEnum(USER_SOURCES).optional(),
                }),
                responses: {
                    200: paginated(UnityUser.transform(deserializeUser)),
                },
            }),
            preference: {
                get: defineEndpoint({
                    method: 'GET',
                    path: '/resources/users/:id/preferences',
                    pathParams: z.object({
                        id: z.string().uuid(),
                    }),
                    responses: {
                        200: Preference,
                    },
                }),
                put: defineEndpoint({
                    method: 'PUT',
                    path: '/resources/users/:id/preferences',
                    pathParams: z.object({
                        id: z.string().uuid(),
                    }),
                    body: Preference,
                    responses: {
                        200: Preference,
                    },
                }),
                patch: defineEndpoint({
                    method: 'PATCH',
                    path: '/resources/users/:id/preferences',
                    pathParams: z.object({
                        id: z.string().uuid(),
                    }),
                    headers: PatchDefaultHeaders.pick({ 'content-type': true }),
                    body: Preference.partial(),
                    responses: {
                        200: Preference,
                    },
                }),
            },
            profile: {
                get: defineEndpoint({
                    method: 'GET',
                    path: '/resources/users/:id/profile',
                    omitContentLanguageFromKey: true,
                    pathParams: z.object({
                        id: z.string().uuid(),
                    }),
                    responses: {
                        200: UnityUser.transform(deserializeUser),
                    },
                }),
                put: defineEndpoint({
                    method: 'PUT',
                    path: '/resources/users/:id/profile',
                    pathParams: z.object({
                        id: z.string().uuid(),
                    }),
                    body: User.transform(serializeUser),
                    responses: {
                        200: UnityUser.transform(deserializeUser),
                    },
                }),
            },
            external: {
                post: defineEndpoint({
                    method: 'POST',
                    path: '/resources/users/external',
                    body: NewUser.transform(serializeUser),
                    responses: {
                        // Backend uses 200 while our tests use 201
                        200: UnityUser.transform(deserializeUser),
                        201: UnityUser.transform(deserializeUser),
                    },
                }),
            },
        },
        agency: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/resources/agencies',
                query: z.object({
                    ...PaginationParams.pick({ size: true }).shape,
                    serviceUser: z.boolean().optional(),
                }),
                responses: {
                    200: paginated(Agency),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/resources/agencies/:id',
                pathParams: z.object({
                    id: z.string().uuid(),
                }),
                responses: {
                    200: Agency,
                },
            }),
        },
        oauth: {
            token: defineEndpoint({
                method: 'POST',
                path: '/oauth/token',
                contentType: 'application/x-www-form-urlencoded',
                body: z.union([z.string(), z.custom<URLSearchParams>()]),
                responses: {
                    200: LoginPayloadResponse,
                    400: z.any(),
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.authUrl,
    },
);
